/**
 * @fileoverview This file defines the `PasswordResetConfirm` React component, which allows users to set a new password after verifying a password reset code using Firebase's `confirmPasswordReset` and `verifyPasswordResetCode` functions. It manages state for password input, loading, errors, and success. The component uses the `oobCode` parameter from the URL to verify the reset request.
 */

import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import '../../styles/components/PasswordReset.css'

export const PasswordResetConfirm: React.FC = () => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(true)
  const [resetting, setResetting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const [oobCode, setOobCode] = useState('')

  const location = useLocation()
  const navigate = useNavigate()
  const auth = getAuth()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const code = params.get('oobCode')

    if (!code) {
      setError('Invalid reset link')
      setLoading(false)
      return
    }

    setOobCode(code)

    // Verify the reset code
    verifyPasswordResetCode(auth, code)
      .then((email) => {
        setEmail(email)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error verifying reset code:', error)
        setError('This password reset link is invalid or has expired.')
        setLoading(false)
      })
  }, [location.search, auth])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setError('')
    setResetting(true)

    try {
      await confirmPasswordReset(auth, oobCode, newPassword)
      setSuccess(true)

      // Redirect to sign in after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (error: any) {
      console.error('Error resetting password:', error)
      setError(error.message || 'Failed to reset password')
    } finally {
      setResetting(false)
    }
  }

  if (loading) {
    return (
      <div className="password-reset-loading">
        <div className="password-reset-loading-text">Loading...</div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="password-reset-card">
          <div className="password-reset-success">
            <h2 className="password-reset-title">Password reset successful!</h2>
            <p>Your password has been reset. Redirecting to sign in...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error && !email) {
    return (
      <div className="auth-container">
        <div className="password-reset-card">
          <div className="password-reset-success">
            <h2 className="password-reset-title">Invalid reset link</h2>
            <div className="error-message">{error}</div>
            <div className="password-reset-back-link">
              <Link to="/auth/reset-password" className="link">
                Request a new reset link
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-container">
      <div className="password-reset-card">
        <Link to="/" className="home-link">
          Ref.
        </Link>
        <h2 className="password-reset-title">Reset your password</h2>
        <p className="password-reset-subtitle">Enter your new password for {email}</p>
        <form className="password-reset-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            disabled={resetting}
            className="cta-button primary password-reset-submit"
          >
            {resetting ? 'Resetting...' : 'Reset password'}
          </button>
        </form>
      </div>
    </div>
  )
}
